// src/services/cornerstoneInit.js

import * as cornerstone from 'cornerstone-core';
import * as cornerstoneTools from 'cornerstone-tools';
import * as cornerstoneMath from 'cornerstone-math';
import * as cornerstoneWADOImageLoader from 'cornerstone-wado-image-loader';
import dicomParser from 'dicom-parser';
import Hammer from 'hammerjs';

let isInitialized = false;

/**
 * Patch AngleTool and CobbAngleTool to prevent crashes when
 * updateCachedStats is called with data from a different tool.
 */
const patchCornerstoneTools = () => {
  // Step 1: Helper to validate a handle point
  const isFinitePoint = (p) =>
    p && Number.isFinite(p.x) && Number.isFinite(p.y);

  // Step 2: Validate Angle measurement structure (start, middle, end)
  const isValidAngleData = (data) => {
    const handles = data?.handles;
    if (!handles) return false;

    const { start, middle, end } = handles;
    return isFinitePoint(start) && isFinitePoint(middle) && isFinitePoint(end);
  };

  // Step 3: Validate CobbAngle measurement structure (start/end + start2/end2)
  const isValidCobbAngleData = (data) => {
    const handles = data?.handles;
    if (!handles) return false;

    const { start, end, start2, end2 } = handles;
    return (
      isFinitePoint(start) &&
      isFinitePoint(end) &&
      isFinitePoint(start2) &&
      isFinitePoint(end2)
    );
  };

  // Step 4: Wrap AngleTool.updateCachedStats with validation + safety guard
  const AngleTool = cornerstoneTools.AngleTool;
  if (AngleTool?.prototype?.updateCachedStats) {
    const originalAngleUpdate = AngleTool.prototype.updateCachedStats;

    AngleTool.prototype.updateCachedStats = function (image, element, data) {
      // Ignore non-Angle measurement data
      if (!isValidAngleData(data)) return;

      // Call original logic safely
      try {
        return originalAngleUpdate.call(this, image, element, data);
      } catch (e) {
        console.warn('[AngleTool] updateCachedStats suppressed error:', e);
      }
    };
  }

  // Step 5: Wrap CobbAngleTool.updateCachedStats with validation + safety guard
  const CobbAngleTool = cornerstoneTools.CobbAngleTool;
  if (CobbAngleTool?.prototype?.updateCachedStats) {
    const originalCobbUpdate = CobbAngleTool.prototype.updateCachedStats;

    CobbAngleTool.prototype.updateCachedStats = function (image, element, data) {
      // Ignore non-CobbAngle measurement data
      if (!isValidCobbAngleData(data)) return;

      // Call original logic safely
      try {
        return originalCobbUpdate.call(this, image, element, data);
      } catch (e) {
        console.warn('[CobbAngleTool] updateCachedStats suppressed error:', e);
      }
    };
  }
};

export const initCornerstone = () => {
  if (isInitialized) return;

  // Step 6: Configure WADO image loader externals
  cornerstoneWADOImageLoader.external.cornerstone = cornerstone;
  cornerstoneWADOImageLoader.external.dicomParser = dicomParser;

  // Step 7: Initialize WADO web workers
  const config = {
    maxWebWorkers: navigator.hardwareConcurrency || 1,
    startWebWorkersOnDemand: true,
    taskConfiguration: {
      decodeTask: {
        initializeCodecsOnStartup: false,
      },
    },
  };
  cornerstoneWADOImageLoader.webWorkerManager.initialize(config);

  // Step 8: Configure cornerstone-tools externals
  cornerstoneTools.external.cornerstone = cornerstone;
  cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
  cornerstoneTools.external.Hammer = Hammer;

  // Step 9: Initialize cornerstone-tools
  cornerstoneTools.init({
    mouseEnabled: true,
    touchEnabled: true,
    globalToolSyncEnabled: false,
    showSVGCursors: false,
  });

  // Step 10: Apply defensive patches to measurement tools
  patchCornerstoneTools();

  isInitialized = true;
  console.log('Cornerstone initialized successfully');
};

export const enableViewportTools = (element) => {
  // Step 11: Register tools for this viewport
  cornerstoneTools.addToolForElement(element, cornerstoneTools.StackScrollMouseWheelTool);
  cornerstoneTools.addToolForElement(element, cornerstoneTools.PanTool);
  cornerstoneTools.addToolForElement(element, cornerstoneTools.ZoomTool, {
    configuration: {
      invert: true,
      preventZoomOutsideImage: false,
      minScale: 0.1,
      maxScale: 20.0,
    },
  });
  cornerstoneTools.addToolForElement(element, cornerstoneTools.WwwcTool);
  cornerstoneTools.addToolForElement(element, cornerstoneTools.LengthTool);
  cornerstoneTools.addToolForElement(element, cornerstoneTools.AngleTool);
  cornerstoneTools.addToolForElement(element, cornerstoneTools.ArrowAnnotateTool);
  cornerstoneTools.addToolForElement(element, cornerstoneTools.CobbAngleTool);

  // Let us handle wheel scrolling ourselves for looping
  cornerstoneTools.setToolPassiveForElement(element, 'StackScrollMouseWheel');
};
